import React, { useMemo } from 'react';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';

import { IconButton } from '@clatter/ui';
import {
  FolderManagerAction,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerItemTypes,
} from '../../../interfaces';
import { getActionsForItemType } from '../../../helpers';

import { FolderManagerItemActions } from './FolderManagerItemActions';

//region INTERFACES
interface HeaderMultiSelectProps {
  selectedItems: FolderManagerItem[];
  isLoading: boolean;
  onClear: () => {};
  onAction: (action: FolderManagerAction) => {};
}

interface FolderManagerHeaderProps {
  filters?: any;
  isLoading: boolean;
  selectedItems: any[];
  onClear: any;
  onAction: any;
  className?: string;
}

//endregion

//region STYLED
const StyledFolderManagerHeader = styled.div`
  width: 100%;
  min-height: 45px;
  margin: 0;
  flex: 1;
  display: flex;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.spacing.large};

  .multiple-selector {
    width: 100%;
    max-width: 450px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 25px;
    background-color: ${({ theme }) => theme?.palette?.accent};
    margin-left: auto;
    min-height: 43.5px;
  }

  .label-container {
    display: flex;
    align-items: center;
    gap: 10px;

    h5 {
      margin: 0;
    }
  }
`;
//endregion

//region COMPONENTS

const HeaderMultiSelect = ({ selectedItems, isLoading, onClear, onAction }: HeaderMultiSelectProps) => {
  const headerActions = useMemo(() => {
    if (selectedItems.length === 0) return [];

    // if the selected items support the `actions` property, use it to determine the available actions.
    const hasActions = selectedItems.some((item) => Array.isArray(item.actions));

    if (hasActions) {
      const actions = selectedItems.reduce<FolderManagerItemAction[]>((acc, item) => {
        const filteredActions =
          item.actions?.filter((action) => action.type === FolderManagerItemActionTypes.MULTI) || [];
        return acc.concat(filteredActions);
      }, []);

      return Array.from(new Map(actions.map((action) => [action.name, action])).values());
    }

    // otherwise, fall back to the previous implementation for compatibility with earlier development logic...
    const includesFolder = selectedItems.some((item) => item.type === FolderManagerItemTypes.FOLDER);
    const includesAsset = selectedItems.some((item) => item.type !== FolderManagerItemTypes.FOLDER);

    const foldersActions = getActionsForItemType(FolderManagerItemTypes.FOLDER, FolderManagerItemActionTypes.MULTI);
    const assetsActions = getActionsForItemType(null, FolderManagerItemActionTypes.MULTI);

    let result: { name: FolderManagerItemActionNames; type: FolderManagerItemActionTypes }[];

    if (includesFolder && includesAsset) {
      result = foldersActions.filter((item1) => assetsActions.some((item2) => item2.name === item1.name));
    } else if (includesFolder) {
      result = foldersActions;
    } else if (includesAsset) {
      result = assetsActions;
    } else {
      result = [];
    }

    return result;
  }, [selectedItems]);

  return (
    <div className="multiple-selector">
      <div className="label-container">
        {/*//@ts-ignore*/}
        <IconButton tooltip="Clear selection" disabled={isLoading} onClick={() => onClear()}>
          <CloseIcon />
        </IconButton>
        <h5>{selectedItems?.length} selected</h5>
      </div>

      <div>
        <FolderManagerItemActions
          actions={headerActions}
          onAction={(data: any) => {
            onAction({ actionType: data.actionType, payload: { selectedItems: selectedItems }, isMultiSelect: true });
            onClear();
          }}
          item={selectedItems[0]}
          isMulti
        />
      </div>
    </div>
  );
};
//endregion

export const FolderManagerHeader = ({
  selectedItems,
  filters,
  isLoading,
  onClear,
  onAction,
  className,
}: FolderManagerHeaderProps) => (
  <StyledFolderManagerHeader className={className}>
    <>
      {filters}
      {selectedItems?.length > 1 && (
        <HeaderMultiSelect selectedItems={selectedItems} onClear={onClear} onAction={onAction} isLoading={isLoading} />
      )}
    </>
  </StyledFolderManagerHeader>
);
